import React, { useEffect, useRef, useState } from 'react';
import './ResultadoRumbo.css';
import { A } from '../ds/components/logoGeometria.js';
import { PUNTOS, FRASES, puntoDe, nombreDe, hitos } from '../lib/rumbo';
import { generarTarjeta } from '../lib/tarjetaRumbo';
import { evento } from '../lib/medicion';

const RAD = Math.PI / 180;
const R = 112; // radio del anillo, en el viewBox de 360

// La aguja del logo gira como un péndulo amortiguado y se clava en el punto del servicio.
// Con prefers-reduced-motion queda ya apuntando.
function useAguja(ref, punto, alLlegar) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const objetivo = punto * 72 * RAD;
    const fijar = (th) => el.setAttribute('transform', `rotate(${th / RAD})`);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { fijar(objetivo); alLlegar(); return undefined; }
    const s = { th: 0, w: 24, t: 0, last: null };
    let raf;
    fijar(0);
    const paso = (ts) => {
      if (s.last === null) s.last = ts;
      const dt = Math.min((ts - s.last) / 1000, 0.05); s.last = ts; s.t += dt;
      const h = 1 / 480;
      for (let i = 0, n = Math.round(dt / h); i < n; i++) {
        s.w += (-22 * Math.sin(s.th - objetivo) - 2.4 * s.w) * h;
        s.th += s.w * h;
      }
      if ((Math.abs(s.w) < 0.05 && Math.abs(Math.sin(s.th - objetivo)) < 0.02) || s.t > 6) { fijar(objetivo); alLlegar(); return; }
      fijar(s.th);
      raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [punto]); // eslint-disable-line react-hooks/exhaustive-deps
}

const Rosa = ({ punto, servicio }) => {
  const aguja = useRef(null);
  const [llego, setLlego] = useState(false);
  useAguja(aguja, punto, () => setLlego(true));
  return (
    <svg className="rb-rosa" viewBox="0 0 360 360" role="img" aria-label={`Tu rumbo: ${servicio}`}>
      <circle cx="180" cy="180" r={R} className="rb-anillo" />
      {PUNTOS.map((nombre, i) => {
        const a = (i * 72 - 90) * RAD, activo = llego && i === punto;
        const x = 180 + R * Math.cos(a), y = 180 + R * Math.sin(a);
        const lx = 180 + (R + 36) * Math.cos(a), ly = 180 + (R + 36) * Math.sin(a) + 6;
        return (
          <g key={nombre} data-activo={activo}>
            <circle cx={x} cy={y} r={activo ? 11 : 6} className="rb-punto" />
            <text x={lx} y={ly} textAnchor="middle" className="rb-punto-nombre">{nombre}</text>
          </g>
        );
      })}
      <g transform="translate(180 180)">
        <g ref={aguja}>
          <path d={A} fillRule="evenodd" className="rb-aguja" transform="scale(1.45) translate(-55.1636 -60.9244)" />
        </g>
      </g>
    </svg>
  );
};

const Marca = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="rb-marca-icono"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);

const ResultadoRumbo = ({ node, collected }) => {
  const servicio = nombreDe(node.servicio);
  const punto = puntoDe(node.servicio);
  const lista = hitos(collected);
  const frase = FRASES[punto];
  const [tarjeta, setTarjeta] = useState(null);
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    let vivo = true;
    generarTarjeta({ rotulo: punto === 4 ? '' : `Mi rumbo: ${servicio}`, frase })
      .then((t) => { if (vivo) setTarjeta(t); })
      .catch(() => { if (vivo) setTarjeta(false); });
    return () => { vivo = false; };
  }, [servicio, punto, frase]);

  // Solo identificadores: la pantalla y el canal. Nunca la frase ni nada escrito por la persona.
  useEffect(() => { evento('rumbo_visto', { pantalla: node.id }); }, [node.id]);

  const textoCompartir = `«${frase.texto}» — ${frase.autor}.${punto === 4 ? '' : ` Mi rumbo: ${servicio}.`}`;
  const archivo = () => new File([tarjeta.blob], 'mi-rumbo-guia.png', { type: 'image/png' });

  const descargar = () => {
    const a = document.createElement('a');
    a.href = tarjeta.url; a.download = 'mi-rumbo-guia.png';
    document.body.appendChild(a); a.click(); a.remove();
    setAviso('Se descargó la imagen.');
    evento('rumbo_compartido', { pantalla: node.id, opcion: 'descarga' });
  };
  const compartir = async () => {
    if (!tarjeta) return;
    try {
      if (navigator.canShare?.({ files: [archivo()] })) { await navigator.share({ files: [archivo()], text: textoCompartir }); evento('rumbo_compartido', { pantalla: node.id, opcion: 'compartir' }); return; }
    } catch (e) { if (e && e.name === 'AbortError') return; }
    descargar();
  };

  return (
    <section className="rb" aria-labelledby="rb-titulo">
      <h2 id="rb-titulo" className="gh-cierre__titulo">Tu rumbo</h2>
      <div className="rb-cuerpo">
        <Rosa punto={punto} servicio={servicio} />
        <ol className="rb-mapa">
          {lista.slice(0, 4).map((h, i) => (
            <li key={h.id} className="rb-hito" style={{ '--d': `${0.5 + i * 0.18}s` }}>
              <span className="rb-marca"><Marca /></span>
              <span className="rb-hito__texto"><span className="rb-hito__rotulo">{h.etiqueta}</span><span className="rb-hito__valor">{h.valor}</span></span>
            </li>
          ))}
          <li className="rb-hito rb-hito--final" style={{ '--d': `${0.5 + Math.min(lista.length, 4) * 0.18}s` }}>
            <span className="rb-marca"><Marca /></span>
            <span className="rb-hito__texto"><span className="rb-hito__rotulo">Tu rumbo</span><span className="rb-hito__valor">{servicio}</span></span>
          </li>
        </ol>
      </div>

      <figure className="rb-frase">
        <blockquote lang="es" className="rb-frase__texto">{frase.texto}</blockquote>
        <figcaption className="rb-frase__autor"><strong>{frase.autor}</strong>, {frase.rol.toLowerCase()}. <span lang="en">«{frase.original}»</span> Traducción libre.</figcaption>
      </figure>

      <div className="rb-tarjeta">
        {tarjeta ? (
          <img className="rb-tarjeta__img" src={tarjeta.url} alt={`Tarjeta para compartir: «${frase.texto}», ${frase.autor}.${punto === 4 ? '' : ` Mi rumbo: ${servicio}.`}`} width="1080" height="1350" />
        ) : (
          <div className="rb-tarjeta__img rb-tarjeta__espera" aria-hidden="true" />
        )}
        {tarjeta !== false && (
          <div className="rb-tarjeta__acciones">
            <p className="rb-tarjeta__texto">Tu frase para empezar.</p>
            <button type="button" className="guia-btn guia-btn--solido" onClick={compartir} disabled={!tarjeta}>Compartir mi frase</button>
            <a className="gh-sutil" href={`https://wa.me/?text=${encodeURIComponent(textoCompartir)}`} target="_blank" rel="noopener noreferrer" onClick={() => evento('rumbo_compartido', { pantalla: node.id, opcion: 'whatsapp' })}>Mandar por WhatsApp →</a>
            {aviso && <p className="gh-nota" role="status">{aviso}</p>}
          </div>
        )}
      </div>
    </section>
  );
};

export default ResultadoRumbo;
